import { useState, type ReactElement, type ReactNode } from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';
import { ApiError, stripBidiControls } from '../lib/api/problem';

export function StateIcon({ kind }: { kind: 'empty' | 'error' | 'locked' | 'warning' }): ReactElement {
  const path = {
    empty: <><path d="M4 7h16v12H4z" /><path d="M8 4h8v3M8 12h8" /></>,
    error: <><path d="M12 3 3 7.5v9L12 21l9-4.5v-9Z" /><path d="M12 8v5M12 17h.01" /></>,
    locked: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" /></>,
    warning: <><path d="M12 3 2.7 20h18.6Z" /><path d="M12 9v5M12 17.5h.01" /></>,
  }[kind];
  return (
    <span className={`state__icon state__icon--${kind}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
    </span>
  );
}

/**
 * Untrusted text, rendered so it cannot reach out of its own box.
 *
 * `<bdi>` is the element for exactly this: a run of text whose direction is not known and must not
 * affect the direction of what surrounds it. React already escapes the characters, so this is not
 * about markup — it is about an unpaired `RLO` in a proxy's error body reversing the Arabic
 * sentence next to it. The controls are stripped and the remainder is isolated.
 */
export function Opaque({ children }: { children: string }): ReactElement {
  return <bdi dir="auto">{stripBidiControls(children)}</bdi>;
}

/**
 * The five universal render states (design/tokens.json §state), implemented once.
 *
 * The rules they encode are not decoration:
 *   - an error renders `message_key` and `code`, never `title`/`detail` as the message;
 *   - a 404 is presented as "outside your scope OR does not exist" because the contract makes the
 *     two indistinguishable, and no screen may imply it can tell them apart;
 *   - an "unavailable" state names the status and the package still to land, and shows NO data.
 */

export function LoadingState({ rows = 3, label }: { rows?: number; label?: string }): ReactElement {
  const { t } = useI18n();
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{label ?? t('state.loading')}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

export function EmptyState({ title, children }: { title?: string; children?: ReactNode }): ReactElement {
  const { t } = useI18n();
  return (
    <div className="state state--empty">
      <StateIcon kind="empty" />
      <p className="state__title">{title ?? t('state.empty')}</p>
      {children ? <div>{children}</div> : null}
    </div>
  );
}

export function LockedState({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return (
    <div className="state state--locked">
      <p className="state__title">
        <StateIcon kind="locked" />
        {title}
      </p>
      {children ? <div>{children}</div> : null}
    </div>
  );
}

export function DegradedState({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return (
    <div className="state state--degraded" role="status">
      <p className="state__title">
        <StateIcon kind="warning" />
        {title}
      </p>
      {children ? <div>{children}</div> : null}
    </div>
  );
}

function CorrelationId({ id }: { id: string }): ReactElement {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <p className="state__meta">
      {t('state.correlation')}: <span className="ltr">{id}</span>{' '}
      <button
        type="button"
        className="btn btn--secondary btn--small"
        onClick={() => {
          void navigator.clipboard?.writeText(id).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        {copied ? t('state.copied') : t('state.copy')}
      </button>
    </p>
  );
}

/**
 * The problem+json surface. Everything the server said is rendered; nothing is paraphrased away.
 */
export function ErrorState({
  error,
  onRetry,
  awaitingPackage,
}: {
  error: unknown;
  onRetry?: () => void;
  /** When the screen knows the surface belongs to a package that has not landed, name it. */
  awaitingPackage?: string;
}): ReactElement {
  const { t } = useI18n();

  if (!(error instanceof ApiError)) {
    return (
      <div className="state state--error" role="alert">
        <p className="state__title">{t('state.error')}</p>
        <p>{error instanceof Error ? error.message : String(error)}</p>
      </div>
    );
  }

  if (awaitingPackage && error.isUnavailable) {
    return (
      <div className="state state--locked" role="status" data-testid="unavailable-state">
        <p className="state__title">
          <StateIcon kind="locked" />
          {t('state.unavailable')}
        </p>
        <p>
          {t('state.unavailable.body', {
            status: error.status,
            path: error.path,
            package: awaitingPackage,
          })}
        </p>
        <p className="state__meta ltr">{error.problem.code}</p>
      </div>
    );
  }

  const fields = error.problem.errors ?? [];
  return (
    <div className="state state--error" role="alert" data-testid="error-state">
      <p className="state__title">
        <StateIcon kind="error" />
        {t('state.error')}
      </p>
      <p>{error.status === 404 ? t('state.forbidden') : t('state.error.body')}</p>
      <details className="state__meta technical-details">
        <summary>{t('state.diagnostics')}</summary>
        <div className="technical-details__body">
          <p>
            <span className="ltr">{error.problem.message_key}</span>
          </p>
          <p>
            <span className="ltr">
              {error.status} · {error.problem.code} · {error.method} {error.path}
            </span>
          </p>
          {fields.length > 0 ? (
            <div>
              <p>{t('state.fields')}</p>
              <ul>
                {fields.map((f) => (
                  <li key={f.field} className="ltr">
                    {f.field}: {f.code} ({f.message_key})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {error.problem.detail ? (
            <div>
              <p>{t('state.detail')}</p>
              <p data-testid="problem-detail">
                <Opaque>{error.problem.detail}</Opaque>
              </p>
            </div>
          ) : null}
        </div>
      </details>
      {error.problem.request_id ? <CorrelationId id={error.problem.request_id} /> : null}
      {onRetry ? (
        <p>
          <button type="button" className="btn btn--secondary btn--small" onClick={onRetry}>
            {t('state.retry')}
          </button>
        </p>
      ) : null}
    </div>
  );
}

export function SuccessNote({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="state state--success" role="status">
      <div>{children}</div>
    </div>
  );
}
